import type { TFunction } from 'i18next';
import { array, object, string } from 'yup';
import {
  createPlatformFee94Error,
  type FormSlice,
  getAudienceFeePercentValue,
  getContextualFeePercentValue,
  getThirdPartyFeePercentValue,
  shouldValidate94Percent,
} from './platformFeeValidation.utils';

const LEADING_ZERO_REGEX = /^0\d+$/;
const DIGITS_AND_DECIMAL_REGEX = /^[\d.]+$/;
const DECIMAL_POINT_REGEX = /\./g;
const MAX_SUM_PERCENT = 94;
const MAX_DECIMAL_PLACES = 1;

const runPlatformFee94Check = (form: FormSlice, message: string): void => {
  if (!shouldValidate94Percent(form)) return;

  const contextualFeePercent = getContextualFeePercentValue(form);
  const audienceFeePercent = getAudienceFeePercentValue(form);

  // curated deal fee alone is already capped at 94% by its own field validation, which has a more specific message
  if (contextualFeePercent + audienceFeePercent === 0) return;

  const curatedDealFeePercent = getThirdPartyFeePercentValue(form);

  const totalFees = contextualFeePercent + audienceFeePercent + curatedDealFeePercent;

  if (totalFees > MAX_SUM_PERCENT) {
    throw createPlatformFee94Error(message);
  }
};

export const createSegmentPartnerPlatformShareSchema = (t: TFunction) => {
  const platformShareLabel = t('Platform Fee');

  return array()
    .nullable()
    .default(null)
    .of(
      object().shape({
        partner_id: string().nullable(),
        share: string()
          .nullable()
          .test({
            message: t('{field} must be a valid number', { field: platformShareLabel }),
            name: 'validNumberFormat',
            test: value => {
              if (value == null || value === '') return true;
              if (!DIGITS_AND_DECIMAL_REGEX.test(value)) return false;
              if ((value.match(DECIMAL_POINT_REGEX) || []).length > 1) return false;

              const num = Number(value);

              return Number.isFinite(num);
            },
          })
          .test({
            message: t('Invalid format - leading zeros not allowed'),
            name: 'noLeadingZeros',
            test: value => {
              if (value == null || value === '') return true;

              return !LEADING_ZERO_REGEX.test(value);
            },
          })
          .test({
            message: t('Invalid format (max 1 decimal)'),
            name: 'maxTwoDecimals',
            test: value => {
              if (value == null || value === '') return true;

              const parts = value.split('.');

              if (parts.length === 2 && parts[1].length > MAX_DECIMAL_PLACES) return false;

              return true;
            },
          })
          .test({
            message: t('{field} must be greater than or equal to {min}', { field: platformShareLabel, min: '0%' }),
            name: 'minValue',
            test: value => {
              if (value == null || value === '') return true;

              const num = Number(value);

              return Number.isFinite(num) && num >= 0;
            },
          })
          .test({
            message: t('{field} must be less than or equal to {max}', {
              field: platformShareLabel,
              max: '94%',
            }),
            name: 'maxValue',
            test: value => {
              if (value == null || value === '') return true;

              const num = Number(value);

              return Number.isFinite(num) && num <= 94;
            },
          }),
      })
    );
};

export const createPlatformFee94Test = (t: TFunction): ((value: unknown, context?: unknown) => boolean) => {
  const message = t(
    'Fees too high: the total of enabled percentage media fees exceeds the allowed limit. Reduce to continue.'
  );

  return (value: unknown, context?: unknown): boolean => {
    const ctx = context as { parent?: unknown } | undefined;
    const form = (value != null && typeof value === 'object' && !Array.isArray(value) ? value : ctx?.parent) as
      | FormSlice
      | undefined;

    if (form == null || typeof form !== 'object' || Array.isArray(form)) {
      return true;
    }

    runPlatformFee94Check(form, message);

    return true;
  };
};
